import { useApolloClient } from "@apollo/client";
import {
  Button,
  ButtonGroup,
  ControlGroup,
  InputGroup,
  NumericInput,
} from "@blueprintjs/core";
import { Select2 } from "@blueprintjs/select";
import _ from "lodash";
import { useEffect, useState } from "react";
import {
  CustomerOrder,
  CustomerOrderPosition_Type,
  Distributor,
} from "../../../../api/schema";
import { valid_number_keys } from "../../../../components/form/builder/OrderPositions";
import { ItemRenderers } from "../../../../components/ItemRenderer";
import { UserLanguage } from "../../../../lang/lang";
import { CustomerOrderAPI } from "../../../../store/CustomerOrder";
import { useDistributors } from "../../../../store/Distributors";

export interface ICreateOrderPositionProps {
  customer_order: CustomerOrder;
  on_create: () => void;
  changes?: (v: boolean) => void;
}

export const CreateOrderPosition = ({
  customer_order,
  on_create,
  changes,
}: ICreateOrderPositionProps) => {
  const [position_text, set_position_text] = useState("");
  const [position_order_number, set_position_order_number] = useState("");
  const [position_quantity, set_position_quantity] = useState(1);
  const [position_price, set_position_price] = useState(0);
  const [button_enabled, set_button_enabled] = useState(false);
  const [position_distributor, set_position_distributor] =
    useState<Distributor | null>(null);

  const { distributors } = useDistributors();

  useEffect(() => {
    let flag = true;
    if (position_text.length === 0) flag = false;
    if (position_quantity === 0) flag = false;

    set_button_enabled(flag);
    changes?.(flag);
  }, [position_text, position_quantity, changes]);

  const { CreatePosition } = CustomerOrderAPI();
  const client = useApolloClient();

  return (
    <ControlGroup key={customer_order.id + "_add_op"}>
      <NumericInput
        placeholder={UserLanguage().data.quantity_scale.quantity}
        onValueChange={(a) => set_position_quantity(a)}
        value={position_quantity}
        leftIcon="full-stacked-chart"
        allowNumericCharactersOnly={true}
        style={{ width: 100 }}
      />
      <InputGroup
        fill
        value={position_text}
        onChange={(e) => set_position_text(e.target.value)}
        placeholder={UserLanguage().data.customerOrder.text}
      />
      <Select2
        items={distributors}
        itemRenderer={ItemRenderers.Distributor_ShortName}
        onItemSelect={set_position_distributor}
        activeItem={position_distributor}
      >
        {position_distributor ? (
          <ControlGroup>
            <Button text={position_distributor.short_name} />
            <Button
              icon="cross"
              onClick={() => set_position_distributor(null)}
            />
          </ControlGroup>
        ) : (
          <Button
            text={UserLanguage().actions.select(
              UserLanguage().data.distributor.label
            )}
          />
        )}
      </Select2>
      <InputGroup
        value={position_order_number}
        onChange={(e) => set_position_order_number(e.target.value)}
        placeholder={UserLanguage().data.customerOrder.order_number}
        style={{ width: 200 }}
      />
      <NumericInput
        placeholder={UserLanguage().data.quantity_scale.price}
        allowNumericCharactersOnly={false}
        buttonPosition="none"
        asyncControl
        style={{ width: 100 }}
        onBlur={(e) => {
          try {
            const n = parseFloat(
              (e.target as HTMLInputElement).value.split(",").join(".")
            );
            set_position_price(n);
          } catch {}
        }}
        onKeyDown={(e) => onKeyDownPrice(e, set_position_price)}
        value={position_price.toFixed(3)}
        leftIcon="euro"
      />
      <NumericInput
        disabled
        value={(position_price * position_quantity).toFixed(3)}
        style={{ width: 100 }}
        buttonPosition="none"
        leftIcon="euro"
      />
      <ButtonGroup>
        <Button
          icon="reset"
          onClick={() => {
            set_position_price(0);
            set_position_quantity(1);
            set_position_text("");
            set_position_order_number("");
            set_position_distributor(null);
          }}
        />
        <Button
          onClick={() =>
            CreatePosition(client, customer_order.id, {
              customer_order_id: customer_order.id,
              price: position_price,
              text: position_text,
              quantity: position_quantity,
              type: CustomerOrderPosition_Type.manual_position,
              article_id: null,
              order_number: position_order_number,
              distributor_id: position_distributor
                ? position_distributor.id
                : null,
            })
              .then((res) => {
                console.log("result", res);
                if (res.data) {
                  on_create();
                  set_button_enabled(false);
                  set_position_price(0);
                  set_position_text("");
                  set_position_order_number("");
                  set_position_quantity(1);
                }
              })
              .catch((e) => console.log("got error", e))
          }
          disabled={!button_enabled}
          icon="add"
          intent="primary"
        />
      </ButtonGroup>
    </ControlGroup>
  );
};

export const onKeyDownPrice = (
  e: React.KeyboardEvent<HTMLInputElement>,
  set_price: (v: number) => void
) => {
  const value = (e.target as HTMLInputElement).value; // (e.target as HTMLInputElement).value;
  console.log("onKeyDown", e, _.get(e.target, "value"));

  if (e.key === "-" && _.indexOf(value, "-") !== -1) e.preventDefault();

  if (e.key === "-") {
    console.log("try parsing float");
    console.log(value);

    try {
      const n = parseFloat(
        (e.target as HTMLInputElement).value.split(",").join(".")
      );
      if (isNaN(n)) e.preventDefault();
    } catch {
      console.log("failed!");
      e.preventDefault();
    }
  }

  if (
    (e.key === "." || e.key === ",") &&
    (_.indexOf(value, ".") !== -1 || _.indexOf(value, ",") !== -1)
  )
    e.preventDefault();

  if (_.indexOf(valid_number_keys, e.key) === -1) {
    e.preventDefault();
    return;
  }
  if (e.key === "Enter" || e.key === "Tab") {
    try {
      const n = parseFloat(
        (e.target as HTMLInputElement).value.split(",").join(".")
      );
      set_price(n);
    } catch {}
  }
};
